import { useState } from 'react';
import {
	Button,
	Descriptions,
	Empty,
	Image,
	Input,
	Modal,
	Segmented,
	Space,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

type ApplicationStatus = 'pending' | 'approved' | 'rejected';

interface PaymentAccount {
	type: 'mobile_money' | 'bank' | null;
	account_number: string | null;
	account_name: string | null;
	bank_name: string | null;
	branch_name: string | null;
}

interface RetailerApplication {
	id: number;
	business_name: string;
	owner_name: string;
	msisdn: string;
	email: string | null;
	district: string;
	town: string;
	shop_number: string;
	latitude: string | null;
	longitude: string | null;
	payment_account: PaymentAccount;
	documents: {
		id_front: string | null;
		id_back: string | null;
		selfie: string | null;
	};
	documents_complete: boolean;
	status: ApplicationStatus;
	rejection_reason: string | null;
	vendor_id: string | null;
	submitted_at: string;
	agent_id: number;
	agent_name: string;
}

const STATUS_COLOUR: Record<ApplicationStatus, string> = {
	pending: 'gold',
	approved: 'green',
	rejected: 'red',
};

const DOCUMENT_LABELS: { key: keyof RetailerApplication['documents']; label: string }[] = [
	{ key: 'id_front', label: 'ID Front' },
	{ key: 'id_back', label: 'ID Back' },
	{ key: 'selfie', label: 'Owner Selfie' },
];

function accountLabel(account: PaymentAccount) {
	if (!account?.type) return <span style={{ color: '#999' }}>Not provided</span>;

	const label = account.type === 'mobile_money' ? 'Mobile Money' : 'Bank';
	return (
		<Space direction="vertical" size={0}>
			<span>
				<Tag color={account.type === 'mobile_money' ? 'blue' : 'purple'}>{label}</Tag>
				{account.account_number}
			</span>
			{account.account_name && (
				<span style={{ fontSize: 12, color: '#888' }}>{account.account_name}</span>
			)}
			{account.bank_name && (
				<span style={{ fontSize: 12, color: '#888' }}>
					{account.bank_name}
					{account.branch_name ? ` — ${account.branch_name}` : ''}
				</span>
			)}
		</Space>
	);
}

export default function FieldRetailerApplications() {
	const queryClient = useQueryClient();

	const [status, setStatus] = useState<ApplicationStatus | 'all'>('pending');
	const [selected, setSelected] = useState<RetailerApplication | null>(null);
	const [rejecting, setRejecting] = useState<RetailerApplication | null>(null);
	const [rejectReason, setRejectReason] = useState('');

	const {
		data: applications = [],
		isLoading,
		error,
	} = useQuery<RetailerApplication[]>({
		queryKey: ['field-retailer-applications', status],
		queryFn: async () => {
			const res = await axios.get('/admin/field/retailer-applications', {
				params: { status },
				withCredentials: true,
			});
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const review = useMutation({
		mutationFn: async (payload: { id: number; action: 'approve' | 'reject'; reason?: string }) => {
			const res = await axios.post(
				`/admin/field/retailer-applications/${payload.id}/review`,
				{ action: payload.action, reason: payload.reason },
				{ withCredentials: true },
			);
			return res.data.results as RetailerApplication;
		},
		onSuccess: (result, variables) => {
			if (variables.action === 'approve') {
				message.success(
					result.vendor_id
						? `Retailer approved — vendor ID ${result.vendor_id}. Credentials sent to the owner.`
						: 'Retailer approved',
				);
			} else {
				message.success('Submission rejected. The agent will see the reason.');
			}
			queryClient.invalidateQueries({ queryKey: ['field-retailer-applications'], exact: false });
			// The approved shop becomes a normal retailer, so that list is stale too.
			queryClient.invalidateQueries({ queryKey: ['retailers'], exact: false });
			setSelected(null);
			setRejecting(null);
			setRejectReason('');
		},
		onError: handleAPIError,
	});

	const confirmApprove = (application: RetailerApplication) => {
		Modal.confirm({
			title: `Approve ${application.business_name}?`,
			width: 520,
			content: (
				<Space direction="vertical" size={8} style={{ marginTop: 8 }}>
					<span>
						This creates the retailer account and a login for {application.owner_name}, and
						sends them their vendor ID and dashboard credentials.
					</span>
					{!application.documents_complete && (
<span>Not all three ID photos are uploaded.</span>
					)}
					{!application.payment_account?.type && (
<span>No payout account — one must be added before they can be paid.</span>
					)}
				</Space>
			),
			okText: 'Approve retailer',
			onOk: () => review.mutateAsync({ id: application.id, action: 'approve' }),
		});
	};

	const columns = [
		{
			title: 'Shop',
			key: 'shop',
			render: (_: unknown, r: RetailerApplication) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontWeight: 500 }}>{r.business_name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>
						{r.town}, {r.district}
						{r.shop_number ? ` — ${r.shop_number}` : ''}
					</span>
				</Space>
			),
		},
		{
			title: 'Owner',
			key: 'owner',
			render: (_: unknown, r: RetailerApplication) => (
				<Space direction="vertical" size={0}>
					<span>{r.owner_name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>{r.msisdn}</span>
				</Space>
			),
		},
		{
			title: 'Payout account',
			key: 'account',
			render: (_: unknown, r: RetailerApplication) => accountLabel(r.payment_account),
		},
		{
			title: 'Documents',
			key: 'documents',
			render: (_: unknown, r: RetailerApplication) =>
				r.documents_complete ? (
					<Tag color="green">Complete</Tag>
				) : (
					<Tooltip title="The agent still has photos to upload">
						<Tag color="orange">
							{DOCUMENT_LABELS.filter((d) => r.documents[d.key]).length}/3
						</Tag>
					</Tooltip>
				),
		},
		{
			title: 'Submitted by',
			key: 'agent',
			render: (_: unknown, r: RetailerApplication) => (
				<Space direction="vertical" size={0}>
					<span>{r.agent_name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>
						{dayjs(r.submitted_at).format('DD MMM YYYY, HH:mm')}
					</span>
				</Space>
			),
		},
		{
			title: 'Status',
			key: 'status',
			render: (_: unknown, r: RetailerApplication) => (
				<Space direction="vertical" size={0}>
					<Tag color={STATUS_COLOUR[r.status]}>{r.status.toUpperCase()}</Tag>
					{r.vendor_id && <span style={{ fontSize: 12 }}>{r.vendor_id}</span>}
					{r.rejection_reason && (
						<span style={{ fontSize: 12, color: '#888' }}>{r.rejection_reason}</span>
					)}
				</Space>
			),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_: unknown, r: RetailerApplication) => (
				<Space>
					<Button size="small" onClick={() => setSelected(r)}>
						Review
					</Button>
					{r.status === 'pending' && (
						<Button size="small" type="primary" onClick={() => confirmApprove(r)}>
							Approve
						</Button>
					)}
				</Space>
			),
		},
	];

	return (
		<AppLayout
			title="Field Retailer Submissions"
			subtitle="Retailers registered by sales agents, awaiting approval"
		>
			<div style={{ padding: 16 }}>
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
						<Segmented
							value={status}
							onChange={(value) => setStatus(value as ApplicationStatus | 'all')}
							options={[
								{ label: 'Pending', value: 'pending' },
								{ label: 'Approved', value: 'approved' },
								{ label: 'Rejected', value: 'rejected' },
								{ label: 'All', value: 'all' },
							]}
						/>
					</Space>

					<Table
						rowKey="id"
						loading={isLoading}
						dataSource={applications}
						columns={columns}
						pagination={{ pageSize: 20 }}
						locale={{
							emptyText: (
								<Empty
									description={
										status === 'pending'
											? 'No submissions waiting for review'
											: `No ${status} submissions`
									}
								/>
							),
						}}
					/>
				</Space>
			</div>

			<Modal
				open={!!selected}
				title={selected?.business_name}
				width={760}
				onCancel={() => setSelected(null)}
				footer={
					selected?.status === 'pending'
						? [
								<Button key="reject" danger onClick={() => setRejecting(selected)}>
									Reject
								</Button>,
								<Button
									key="approve"
									type="primary"
									loading={review.isPending}
									onClick={() => selected && confirmApprove(selected)}
								>
									Approve retailer
								</Button>,
						  ]
						: [
								<Button key="close" onClick={() => setSelected(null)}>
									Close
								</Button>,
						  ]
				}
			>
				{selected && (
					<Space direction="vertical" size={16} style={{ width: '100%' }}>
						<Descriptions size="small" column={2} bordered>
							<Descriptions.Item label="Owner">{selected.owner_name}</Descriptions.Item>
							<Descriptions.Item label="Phone">{selected.msisdn}</Descriptions.Item>
							<Descriptions.Item label="Email">
								{selected.email || <span style={{ color: '#999' }}>Not provided</span>}
							</Descriptions.Item>
							<Descriptions.Item label="Shop number">
								{selected.shop_number || '—'}
							</Descriptions.Item>
							<Descriptions.Item label="Location">
								{selected.town}, {selected.district}
							</Descriptions.Item>
							<Descriptions.Item label="Coordinates">
								{selected.latitude ? (
									<a
										href={`https://maps.google.com/?q=${selected.latitude},${selected.longitude}`}
										target="_blank"
										rel="noreferrer"
									>
										{selected.latitude}, {selected.longitude}
									</a>
								) : (
									<Tooltip title="No usable GPS fix">
										<span style={{ color: '#999' }}>Not captured</span>
									</Tooltip>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Payout account" span={2}>
								{accountLabel(selected.payment_account)}
							</Descriptions.Item>
							<Descriptions.Item label="Submitted by" span={2}>
								{selected.agent_name} ·{' '}
								{dayjs(selected.submitted_at).format('DD MMM YYYY, HH:mm')}
							</Descriptions.Item>
						</Descriptions>

						<div>
							<h4 style={{ marginBottom: 8 }}>Identity documents</h4>
							<Space size={12} wrap>
								{DOCUMENT_LABELS.map((doc) => (
									<Space key={doc.key} direction="vertical" size={4} align="center">
										{selected.documents[doc.key] ? (
											<Image
												src={selected.documents[doc.key] as string}
												width={190}
												height={140}
												style={{ objectFit: 'cover', borderRadius: 6 }}
											/>
										) : (
											<div
												style={{
													width: 190,
													height: 140,
													borderRadius: 6,
													border: '1px dashed #d9d9d9',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													color: '#999',
													fontSize: 12,
												}}
											>
												Not uploaded
											</div>
										)}
										<span style={{ fontSize: 12 }}>{doc.label}</span>
									</Space>
								))}
							</Space>
						</div>
					</Space>
				)}
			</Modal>

			<Modal
				open={!!rejecting}
				title={`Reject ${rejecting?.business_name}?`}
				okText="Reject submission"
				okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
				confirmLoading={review.isPending}
				onCancel={() => {
					setRejecting(null);
					setRejectReason('');
				}}
				onOk={() =>
					rejecting &&
					review.mutate({ id: rejecting.id, action: 'reject', reason: rejectReason.trim() })
				}
			>
				<Space direction="vertical" size={8} style={{ width: '100%' }}>
					<span>
						The agent who made this trip will see the reason, so make it something they can
						act on.
					</span>
					<Input.TextArea
						rows={3}
						value={rejectReason}
						onChange={(e) => setRejectReason(e.target.value)}
						placeholder="e.g. Shop already covered by another retailer"
					/>
				</Space>
			</Modal>
		</AppLayout>
	);
}
